/**
 * v3.qc — /v3/qc/* (4 live + 1 gated POST /v3/qc/accounts). Upstream: Dataverse pcb-risk.
 */

import type {
  RequestOptions,
  QcExceptionTaskDto,
  AddQcNotePayload,
  AddQcNoteResult,
  ReassignProcessorResult,
  Iso8601,
} from '@retail-ops/shared-types';
import type { ApiHttpClient } from '../http.js';

export interface QcListResponse {
  success: boolean;
  tasks: ReadonlyArray<QcExceptionTaskDto>;
  error?: string;
}

export interface QcCompleteResult {
  success: boolean;
  completedAt?: Iso8601;
  error?: string;
}

export interface QcCreateAccountRequest {
  AccountNumber: string;
  OfficerSystemUserId: string;
  ProcessorSystemUserId: string;
  BranchId: string;
  AccountStatus: number;
  CallClass: string;
  ProductType: string;
  QcType: string;
  GlCode: string;
  OpenDate: string;
}

export interface QcCreateAccountResponse {
  success: boolean;
  accountId?: string;
  error?: string;
  featureEnabled?: boolean;
}

export class QcApi {
  public constructor(private readonly http: ApiHttpClient) {}

  public listOpenTasks(
    filters: { officerEmail?: string; status?: number; top?: number },
    opts?: RequestOptions
  ): Promise<QcListResponse> {
    return this.http.get<QcListResponse>('/v3/qc/tasks', {
      ...opts,
      query: filters as unknown as Record<string, string | number | boolean | undefined>,
    });
  }

  public completeTask(
    taskId: string,
    req: { officerEmail: string },
    opts?: RequestOptions
  ): Promise<QcCompleteResult> {
    return this.http.patch<QcCompleteResult>(
      `/v3/qc/tasks/${encodeURIComponent(taskId)}/complete`,
      { ...opts, body: req }
    );
  }

  public reassignProcessor(
    accountId: string,
    req: { NewProcessorEmail: string; RequesterEmail: string },
    opts?: RequestOptions
  ): Promise<ReassignProcessorResult> {
    return this.http.patch<ReassignProcessorResult>(
      `/v3/qc/accounts/${encodeURIComponent(accountId)}/processor`,
      { ...opts, body: req }
    );
  }

  public addNote(req: AddQcNotePayload, opts?: RequestOptions): Promise<AddQcNoteResult> {
    return this.http.post<AddQcNoteResult>('/v3/qc/notes', { ...opts, body: req });
  }

  /** 503 unless Dataverse__EnableAccountCreate=true. Risk-team sign-off required. */
  public createAccount(
    req: QcCreateAccountRequest,
    opts?: RequestOptions
  ): Promise<QcCreateAccountResponse> {
    return this.http.post<QcCreateAccountResponse>('/v3/qc/accounts', { ...opts, body: req });
  }

  /**
   * List QC notes for an exception. Backs the ExceptionDetail right-rail
   * notes thread.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public listNotes(exceptionId: string, opts?: RequestOptions): Promise<any> {
    return this.http.get(`/v3/qc/exceptions/${encodeURIComponent(exceptionId)}/notes`, opts);
  }
}
